// Greeting cards (D144): a festival or family occasion, drawn on the device
// as a 1080×1080 PNG and sent like any photo, so it's end-to-end encrypted.
// The sheet is context based (D176): family occasions are always there,
// festivals only around their days, from a dated table (festivalDates.ts).
// Every occasion has several designs and ready-made wishes, and the days
// inside Diwali, Navratri and Durga Puja have their own (D269).

import {
  currentLanguage,
  t,
  translate,
  type Language,
  type MessageKey,
} from '../i18n';
import greetingsTable from '../../../shared/contracts/greetings.json';
import {
  festivalDaysFor,
  type FestivalDays,
  type FestivalId,
} from './festivalDates';
import { festivalRegion, type FestivalRegion } from './festivalRegion';

/** Family occasions, offered every day (D176). */
export type EverydayOccasionId =
  | 'birthday'
  | 'anniversary'
  | 'get-well'
  | 'congratulations'
  | 'thank-you';

export type OccasionId = FestivalId | EverydayOccasionId;

/** The fixed ways a card can be laid out (D269); the native apps draw the same. */
export type CardLayout =
  | 'centre'
  | 'corner-motif'
  | 'border-pattern'
  | 'big-motif';

export const CARD_LAYOUTS: readonly CardLayout[] = [
  'centre',
  'corner-motif',
  'border-pattern',
  'big-motif',
];

export type WishTone = 'formal' | 'warm' | 'from-all-of-us' | 'playful';

/** One look for a card: a motif, solid Living Room colours and a layout. */
export interface CardDesign {
  id: string;
  layout: CardLayout;
  /** One emoji, or a small set the pattern layouts cycle through. */
  motif: string[];
  ground: string;
  ink: string;
  accent: string;
}

/** A ready-made line: English, and the Hindi where it's natural. */
export interface Wish {
  tone: WishTone;
  /** May hold ", {name}": the recipient in a direct chat, or dropped. */
  en: string;
  hi?: string;
}

export type FestivalDayId =
  | 'dhanteras'
  | 'chhoti-diwali'
  | 'lakshmi-puja'
  | 'govardhan-puja'
  | 'bhai-dooj'
  | 'navratri-ashtami'
  | 'navratri-navami'
  | 'durga-shashthi'
  | 'durga-saptami'
  | 'durga-ashtami'
  | 'durga-navami'
  | 'vijayadashami';

/**
 * A day inside a multi-day festival (D269), placed from the festival's
 * first and last days in the region's table: "first+2", "last-1"; the
 * earlier of `at` and `atLatest` where both are given.
 */
export interface FestivalDay {
  id: FestivalDayId;
  /** On the card, above the greeting. */
  name: string;
  at: string;
  atLatest?: string;
  /** Leads when another day falls on the same date (Lakshmi Puja). */
  main?: boolean;
  designs: CardDesign[];
  wishes: Wish[];
}

export interface Occasion {
  id: OccasionId;
  /** Its name, above the greeting on the card. */
  name: string;
  /** Its name in Hindi, for the sheet when the app is in Hindi (D269). */
  nameHi?: string;
  designs: CardDesign[];
  wishes: Wish[];
  days?: FestivalDay[];
}

interface GreetingsFile {
  layouts: CardLayout[];
  tones: WishTone[];
  occasions: Occasion[];
}

// The designs and wishes are shared/contracts/greetings.json (D269), so the
// native apps bundle the same set. Grounds are the theme's own solids:
// oxblood, saffron, stone and the soft avatar pairs. No gradient washes
// (Living Room).
const FILE = greetingsTable as GreetingsFile;

export const GREETING_TONES: readonly WishTone[] = FILE.tones;
export const CONTRACT_LAYOUTS: readonly CardLayout[] = FILE.layouts;
export const OCCASIONS: readonly Occasion[] = FILE.occasions;

const EVERYDAY: readonly OccasionId[] = [
  'birthday',
  'anniversary',
  'get-well',
  'congratulations',
  'thank-you',
];

export function isEveryday(occasion: Occasion): boolean {
  return EVERYDAY.includes(occasion.id);
}

// The first occasions' names have catalog keys (D146); every other one is
// named from the file, `name` or `nameHi` by the app's language (D269).
const OCCASION_NAMES: Partial<Record<OccasionId, MessageKey>> = {
  diwali: 'chat.greeting.diwali',
  holi: 'chat.greeting.holi',
  'raksha-bandhan': 'chat.greeting.rakshaBandhan',
  eid: 'chat.greeting.eid',
  'eid-al-adha': 'chat.greeting.eidAlAdha',
  christmas: 'chat.greeting.christmas',
  pongal: 'chat.greeting.pongal',
  onam: 'chat.greeting.onam',
  navratri: 'chat.greeting.navratri',
  lohri: 'chat.greeting.lohri',
  'durga-puja': 'chat.greeting.durgaPuja',
  'karva-chauth': 'chat.greeting.karvaChauth',
  'new-year': 'chat.greeting.newYear',
  birthday: 'chat.greeting.birthday',
  anniversary: 'chat.greeting.anniversary',
  'get-well': 'chat.greeting.getWell',
  congratulations: 'chat.greeting.congratulations',
  'thank-you': 'chat.greeting.thankYou',
};

export const FESTIVAL_DAY_NAMES: Record<FestivalDayId, MessageKey> = {
  dhanteras: 'chat.greeting.day.dhanteras',
  'chhoti-diwali': 'chat.greeting.day.chhotiDiwali',
  'lakshmi-puja': 'chat.greeting.day.lakshmiPuja',
  'govardhan-puja': 'chat.greeting.day.govardhanPuja',
  'bhai-dooj': 'chat.greeting.day.bhaiDooj',
  'navratri-ashtami': 'chat.greeting.day.navratriAshtami',
  'navratri-navami': 'chat.greeting.day.navratriNavami',
  'durga-shashthi': 'chat.greeting.day.durgaShashthi',
  'durga-saptami': 'chat.greeting.day.durgaSaptami',
  'durga-ashtami': 'chat.greeting.day.durgaAshtami',
  'durga-navami': 'chat.greeting.day.durgaNavami',
  vijayadashami: 'chat.greeting.day.vijayadashami',
};

/**
 * The occasion's name in the app's language, for the picker (D146): its
 * catalog key where it has one, otherwise the file's `nameHi` in Hindi and
 * `name` in English. The card itself keeps `name`: its lines are already
 * bilingual.
 */
export function occasionName(
  occasion: Occasion,
  language: Language = currentLanguage()
): string {
  const key = OCCASION_NAMES[occasion.id];
  if (key) return translate(language, key);
  return language === 'hi' && occasion.nameHi ? occasion.nameHi : occasion.name;
}

/** Whether an occasion's name comes from the catalogs rather than the file. */
export function hasCatalogName(occasion: Occasion): boolean {
  return OCCASION_NAMES[occasion.id] !== undefined;
}

/** A festival day's name in the app's language ("Lakshmi Puja"). */
export function festivalDayName(day: FestivalDay): string {
  return t(FESTIVAL_DAY_NAMES[day.id]);
}

export function findOccasion(id: string | null | undefined): Occasion | null {
  return OCCASIONS.find((occasion) => occasion.id === id) ?? null;
}

/**
 * A wish as the card's line: "Happy birthday, Dadi · जन्मदिन मुबारक". Without
 * a recipient, ", {name}" goes: "Happy birthday · जन्मदिन मुबारक".
 */
export function wishLine(wish: Wish, recipient?: string): string {
  const name = recipient?.trim();
  const english = wish.en.replace(/,\s*\{name\}/g, name ? `, ${name}` : '');
  return wish.hi ? `${english} · ${wish.hi}` : english;
}

/** A design as the sheet offers it, with what it prints above the greeting. */
export interface CardChoice {
  occasion: OccasionId;
  day?: FestivalDayId;
  design: CardDesign;
  /** Above the greeting: the day's name ("Lakshmi Puja") or the occasion's. */
  label: string;
  /** What the card says when the line is left empty. */
  fallback: string;
  /** The wishes that go with it: its day's, or the occasion's own. */
  wishes: Wish[];
}

/**
 * What the sheet offers for an occasion: today's festival days' designs and
 * wishes first, then the occasion's own (D269).
 */
export function cardOptions(
  occasion: Occasion,
  days: readonly FestivalDay[] = [],
  /**
   * Whether the first day leads, ahead of the festival's own cards: during
   * the festival, the likeliest day for today. Before or after it, the
   * festival's own cards come first and every day follows.
   */
  leads = days.length > 0
): { cards: CardChoice[]; wishes: Wish[] } {
  const own = {
    day: undefined,
    name: occasion.name,
    designs: occasion.designs,
    wishes: occasion.wishes,
  };
  const asSet = (day: FestivalDay) => ({ ...day, day: day.id });
  const sets =
    leads && days[0]
      ? [asSet(days[0]), own, ...days.slice(1).map(asSet)]
      : [own, ...days.map(asSet)];
  const cards: CardChoice[] = sets.flatMap((set) =>
    set.designs.map((design) => ({
      occasion: occasion.id,
      ...(set.day ? { day: set.day } : {}),
      design,
      label: set.name,
      fallback: wishLine(set.wishes[0] ?? occasion.wishes[0]!),
      wishes: set.wishes,
    }))
  );
  return { cards, wishes: sets.flatMap((set) => set.wishes) };
}

/**
 * The line a card starts with: the first wish (the leading day's, if any).
 * A wish to one person with ", {name}" names them: "Happy birthday, Dadi ·
 * जन्मदिन मुबारक", "Happy Bhai Dooj, Didi · …" (D269, D315); the wishes
 * without one are for everyone.
 */
export function defaultLine(
  occasion: Occasion,
  recipient?: string,
  days: readonly FestivalDay[] = [],
  leads = days.length > 0
): string {
  const first = cardOptions(occasion, days, leads).wishes[0]!;
  return wishLine(first, recipient);
}

// ---- When festivals show (D176) ----

/** Where today falls around a festival: just before, on one of its days, or just after. */
export type FestivalTiming = 'soon' | 'today' | 'belated';

const DAYS_BEFORE = 3;
const DAYS_AFTER = 2;
/**
 * The Islamic days follow the moon sighting, which can move them a day
 * either way (D176).
 */
export const MOON_SLACK: Partial<Record<FestivalId, number>> = {
  eid: 1,
  'eid-al-adha': 1,
  'ramadan-start': 1,
  'shab-e-barat': 1,
  'shab-e-qadr': 1,
  'milad-un-nabi': 1,
  'islamic-new-year': 1,
};

// Days since the epoch for a calendar date, so the sums ignore time zones
// and daylight saving.
const dayNumber = (year: number, month: number, date: number) =>
  Math.round(Date.UTC(year, month - 1, date) / 86_400_000);
const parseDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return dayNumber(y!, m!, d!);
};
const isoDay = (n: number) =>
  new Date(n * 86_400_000).toISOString().slice(0, 10);
const localDay = (now: Date) =>
  dayNumber(now.getFullYear(), now.getMonth() + 1, now.getDate());

/**
 * Whether `now` (the device's local date) is in the festival's window:
 * from 3 days before its first day to 2 days after its last, a day wider
 * each way for Eid. Null outside it, or where the table has no dates.
 * The dates are those of the device's region (D266) unless one is given.
 */
export function festivalTiming(
  id: FestivalId,
  now = new Date(),
  region: FestivalRegion = festivalRegion(now)
): FestivalTiming | null {
  return festivalWindow(id, now, region)?.timing ?? null;
}

function festivalWindow(
  id: FestivalId,
  now: Date,
  region: FestivalRegion
): { timing: FestivalTiming; first: number; days: FestivalDays } | null {
  const today = localDay(now);
  const slack = MOON_SLACK[id] ?? 0;
  // Last year's or next year's festival can reach across New Year.
  for (const year of [
    now.getFullYear() - 1,
    now.getFullYear(),
    now.getFullYear() + 1,
  ]) {
    const days = festivalDaysFor(region, id, year);
    if (!days) continue;
    const first = parseDay(days.first);
    const last = parseDay(days.last);
    if (today >= first && today <= last) {
      return { timing: 'today', first, days };
    }
    if (today < first && today >= first - DAYS_BEFORE - slack) {
      return { timing: 'soon', first, days };
    }
    if (today > last && today <= last + DAYS_AFTER + slack) {
      return { timing: 'belated', first, days };
    }
  }
  return null;
}

// "first+2", "last-1", "first", "last".
function placeDay(rule: string, first: number, last: number): number {
  const match = /^(first|last)(?:([+-])(\d+))?$/.exec(rule);
  if (!match) throw new Error(`Bad festival day rule: ${rule}`);
  const base = match[1] === 'first' ? first : last;
  const offset = Number(match[3] ?? 0) * (match[2] === '-' ? -1 : 1);
  return base + offset;
}

/**
 * The date of a festival's day (D269) within the festival's days, or null
 * where it falls outside them (a short festival has no room for it).
 */
export function festivalDayDate(
  day: Pick<FestivalDay, 'at' | 'atLatest'>,
  days: FestivalDays
): string | null {
  const first = parseDay(days.first);
  const last = parseDay(days.last);
  let at = placeDay(day.at, first, last);
  if (day.atLatest) at = Math.min(at, placeDay(day.atLatest, first, last));
  return at >= first && at <= last ? isoDay(at) : null;
}

/**
 * All of a festival's days, likeliest for `today` first (D269): by how far
 * the region table puts each from today, the `main` day (Lakshmi Puja)
 * first on a tie, then in calendar order. No day is ever left out: the
 * table can be a day off where you are, so a card is never hidden over it.
 */
function daysByLikelihood(
  occasion: Occasion,
  festival: FestivalDays,
  today: number
): FestivalDay[] {
  const all = occasion.days ?? [];
  const distance = (day: FestivalDay) => {
    const date = festivalDayDate(day, festival);
    return date === null ? Infinity : Math.abs(parseDay(date) - today);
  };
  return [...all].sort(
    (a, b) =>
      distance(a) - distance(b) ||
      Number(!!b.main) - Number(!!a.main) ||
      all.indexOf(a) - all.indexOf(b)
  );
}

/**
 * Every day of the festival while it's in its window (D176), the likeliest
 * for `now` first (Lakshmi Puja on Diwali's third day), by the device's
 * region's dates (D266) unless one is given. Empty outside the window.
 */
export function festivalDaysOn(
  id: FestivalId,
  now = new Date(),
  region: FestivalRegion = festivalRegion(now)
): FestivalDay[] {
  const occasion = findOccasion(id);
  const window = festivalWindow(id, now, region);
  if (!occasion || !window) return [];
  return daysByLikelihood(occasion, window.days, localDay(now));
}

export interface SeasonalOccasion {
  occasion: Occasion;
  timing: FestivalTiming;
  /** All the festival's days, the likeliest for today first (D269). */
  days: FestivalDay[];
  /**
   * Whether the first day's cards lead, ahead of the festival's own: on the
   * festival's days. Before and after it, the festival's own cards lead.
   */
  dayLeads: boolean;
}

const TIMING_ORDER: Record<FestivalTiming, number> = {
  today: 0,
  soon: 1,
  belated: 2,
};

/**
 * What the sheet offers on `now`: the festivals in their window, today's
 * first, then the coming ones, then the belated (earliest first within
 * each), and the family occasions that are always there. Festival dates
 * are the device's region's (D266) unless one is given.
 */
export function greetingChoices(
  now = new Date(),
  region: FestivalRegion = festivalRegion(now)
): {
  seasonal: SeasonalOccasion[];
  always: Occasion[];
} {
  const seasonal: Array<SeasonalOccasion & { first: number }> = [];
  for (const occasion of OCCASIONS) {
    if (isEveryday(occasion)) continue;
    const window = festivalWindow(occasion.id as FestivalId, now, region);
    if (!window) continue;
    const days = daysByLikelihood(occasion, window.days, localDay(now));
    seasonal.push({
      occasion,
      timing: window.timing,
      first: window.first,
      days,
      dayLeads: window.timing === 'today' && days.length > 0,
    });
  }
  seasonal.sort(
    (a, b) =>
      TIMING_ORDER[a.timing] - TIMING_ORDER[b.timing] || a.first - b.first
  );
  return {
    seasonal: seasonal.map(({ first: _first, ...rest }) => rest),
    always: OCCASIONS.filter(isEveryday),
  };
}

/**
 * Whether a day-and-month birthday falls on `now`'s local date. A 29
 * February birthday is kept on 28 February in other years.
 */
export function isBirthdayToday(
  day: number | null | undefined,
  month: number | null | undefined,
  now = new Date()
): boolean {
  if (!day || !month) return false;
  const today = now.getDate();
  const thisMonth = now.getMonth() + 1;
  if (day === today && month === thisMonth) return true;
  const year = now.getFullYear();
  const leap = new Date(year, 1, 29).getMonth() === 1;
  return day === 29 && month === 2 && !leap && thisMonth === 2 && today === 28;
}

/** The first word of a name, for "— Asha". */
export function firstName(name: string | null | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

export const MAX_LINE_LENGTH = 120;

/** Splits text into lines no wider than maxWidth, by words; a single word too wide keeps its own line. */
export function wrapText(
  text: string,
  maxWidth: number,
  measure: (text: string) => number
): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (line && measure(candidate) > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) lines.push(line);
  }
  return lines;
}

/**
 * A font size and line breaks for the text: as few lines as it can take
 * while staying at least three quarters of `max` ("Merry Christmas" on one
 * line rather than two), otherwise the largest size at which it fits
 * `maxLines` lines of `maxWidth`. At `min` it gives up and returns the
 * lines as they fall.
 */
export function fitText(
  text: string,
  options: {
    maxWidth: number;
    maxLines: number;
    max: number;
    min: number;
    step?: number;
    /** Width of `text` at font size `size`. */
    measure: (text: string, size: number) => number;
  }
): { size: number; lines: string[] } {
  const { maxWidth, maxLines, max, min, step = 4, measure } = options;
  const largestFor = (lineCount: number, floor: number) => {
    for (let size = max; size >= floor; size -= step) {
      const lines = wrapText(text, maxWidth, (t) => measure(t, size));
      if (
        lines.length <= lineCount &&
        lines.every((line) => measure(line, size) <= maxWidth)
      ) {
        return { size, lines };
      }
    }
    return null;
  };
  const preferred = Math.max(min, max * 0.75);
  for (let lineCount = 1; lineCount < maxLines; lineCount += 1) {
    const fit = largestFor(lineCount, preferred);
    if (fit) return fit;
  }
  return (
    largestFor(maxLines, min) ?? {
      size: min,
      lines: wrapText(text, maxWidth, (t) => measure(t, min)),
    }
  );
}

// ---- Drawing ----

export const CARD_SIZE = 1080;
const PADDING = 96;
const CENTRE = CARD_SIZE / 2;
const DISPLAY_FONT = `'Bricolage Grotesque Variable', 'Noto Sans Devanagari Variable', 'Hanken Grotesk Variable', system-ui, sans-serif`;
const BODY_FONT = `'Hanken Grotesk Variable', 'Noto Sans Devanagari Variable', system-ui, sans-serif`;
const EMOJI_FONT = `'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif`;

const font = (weight: number, size: number, family: string) =>
  `${weight} ${size}px ${family}`;

/** Waits for the card's fonts, so the first card isn't drawn in a fallback. */
export async function loadCardFonts(text: string): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  try {
    await Promise.all([
      document.fonts.load(font(800, 96, DISPLAY_FONT), text),
      document.fonts.load(font(700, 72, DISPLAY_FONT), text),
      document.fonts.load(font(600, 48, BODY_FONT), text),
    ]);
    await document.fonts.ready;
  } catch {
    // Draw with whatever is there rather than not at all.
  }
}

/** A rectangle on the 1080 card. */
export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** A piece of text as it will be drawn: centred on `x`, baseline at `y`. */
export interface PlacedText {
  text: string;
  font: string;
  x: number;
  y: number;
  /** Its ink, measured: width and the glyphs' ascent and descent. */
  box: Box;
}

export interface PlacedMotif {
  text: string;
  x: number;
  y: number;
  size: number;
  box: Box;
}

/**
 * Where everything on a card goes (D269): worked out before anything is
 * drawn, so the greeting can be shrunk until it fits, and so tests can
 * check the layout with real fonts without reading pixels.
 */
export interface CardGeometry {
  label: PlacedText;
  /** The greeting: the English lines, then the rest (usually Hindi). */
  lines: PlacedText[];
  signature: PlacedText | null;
  motifs: PlacedMotif[];
  /** What the words keep clear of: the motifs, the disc, the pattern bands. */
  reserved: Box[];
  /** Inside the printed frame; every word stays in it. */
  safe: Box;
  /** The greeting's (English) size, after fitting. */
  size: number;
}

/** Measures text in a CSS font: its width and the glyphs' ascent and descent. */
export type MeasureText = (
  text: string,
  font: string
) => { width: number; ascent: number; descent: number };

/** Gaps the greeting keeps: from the signature, and from everything else. */
export const SIGNATURE_GAP = 28;
export const TEXT_GAP = 10;
const SAFE: Box = { left: 56, top: 56, right: 1024, bottom: 1024 };
const MIN_LEAD = 40;
const MIN_REST = 28;

/**
 * Each layout's places (in 1080 units): the label, the band the greeting is
 * centred in, the signature, the largest the greeting may be, its motifs
 * and what else the words must avoid. Decoration is solid colour on a
 * solid ground (Living Room).
 */
interface LayoutFrame {
  labelY: number;
  textTop: number;
  textBottom: number;
  leadMax: number;
  signatureY: number;
  motifs: (design: CardDesign) => Array<Omit<PlacedMotif, 'box'>>;
  reserved: Box[];
  decorate: (ctx: CanvasRenderingContext2D, design: CardDesign) => void;
}

const motifAt = (design: CardDesign, i: number) =>
  design.motif[i % design.motif.length] ?? '';

// An emoji drawn `size` px centred on (x, y) stays inside this box.
const motifBox = (x: number, y: number, size: number): Box => ({
  left: x - size * 0.6,
  top: y - size * 0.6,
  right: x + size * 0.6,
  bottom: y + size * 0.6,
});

function frame(
  ctx: CanvasRenderingContext2D,
  colour: string,
  inset: number,
  width: number,
  alpha: number
) {
  ctx.strokeStyle = colour;
  ctx.lineWidth = width;
  ctx.globalAlpha = alpha;
  roundRect(
    ctx,
    inset,
    inset,
    CARD_SIZE - inset * 2,
    CARD_SIZE - inset * 2,
    48
  );
  ctx.stroke();
  ctx.globalAlpha = 1;
}

const PATTERN_COUNT = 7;
const PATTERN_STEP = (CARD_SIZE - PADDING * 2) / (PATTERN_COUNT - 1);
const DISC = { x: CENTRE, y: 300, r: 200 };

export const LAYOUTS: Record<CardLayout, LayoutFrame> = {
  // The first look (D144): a large motif above the name and the greeting.
  centre: {
    labelY: 450,
    textTop: 480,
    textBottom: 900,
    leadMax: 120,
    signatureY: 960,
    motifs: (design) => [
      { text: motifAt(design, 0), x: CENTRE, y: 250, size: 220 },
    ],
    reserved: [],
    decorate: (ctx, design) => frame(ctx, design.accent, 40, 4, 0.6),
  },
  // A double frame with the motif tucked into two opposite corners; the
  // greeting ends above the lower corner's motif.
  'corner-motif': {
    labelY: 340,
    textTop: 370,
    textBottom: 790,
    leadMax: 124,
    signatureY: 900,
    motifs: (design) => [
      { text: motifAt(design, 0), x: 190, y: 190, size: 130 },
      {
        text: motifAt(design, design.motif.length - 1),
        x: CARD_SIZE - 190,
        y: CARD_SIZE - 190,
        size: 130,
      },
    ],
    reserved: [],
    decorate: (ctx, design) => {
      frame(ctx, design.accent, 40, 4, 0.7);
      frame(ctx, design.accent, 60, 2, 0.4);
    },
  },
  // A row of small motifs along the top and bottom, like a printed border,
  // each set off by a rule in the accent.
  'border-pattern': {
    labelY: 290,
    textTop: 320,
    textBottom: 770,
    leadMax: 120,
    signatureY: 836,
    motifs: (design) =>
      Array.from({ length: PATTERN_COUNT * 2 }, (_, i) => {
        const top = i < PATTERN_COUNT;
        const col = i % PATTERN_COUNT;
        return {
          text: motifAt(design, top ? col : col + 1),
          x: PADDING + PATTERN_STEP * col,
          y: top ? 120 : CARD_SIZE - 120,
          size: 72,
        };
      }),
    // The bands with the motifs and the rules, top and bottom.
    reserved: [
      { left: 0, top: 0, right: CARD_SIZE, bottom: 204 },
      { left: 0, top: CARD_SIZE - 204, right: CARD_SIZE, bottom: CARD_SIZE },
    ],
    decorate: (ctx, design) => {
      ctx.fillStyle = design.accent;
      ctx.fillRect(PADDING, 196, CARD_SIZE - PADDING * 2, 4);
      ctx.fillRect(PADDING, CARD_SIZE - 200, CARD_SIZE - PADDING * 2, 4);
    },
  },
  // The motif big on a solid disc in the accent; the greeting below it.
  'big-motif': {
    labelY: 570,
    textTop: 600,
    textBottom: 950,
    leadMax: 104,
    signatureY: 1000,
    motifs: (design) => [
      { text: motifAt(design, 0), x: DISC.x, y: DISC.y + 10, size: 230 },
    ],
    reserved: [
      {
        left: DISC.x - DISC.r,
        top: DISC.y - DISC.r,
        right: DISC.x + DISC.r,
        bottom: DISC.y + DISC.r,
      },
    ],
    decorate: (ctx, design) => {
      ctx.fillStyle = design.accent;
      ctx.beginPath();
      ctx.arc(DISC.x, DISC.y, DISC.r, 0, Math.PI * 2);
      ctx.fill();
    },
  },
};

const overlaps = (a: Box, b: Box, gap = 0) =>
  a.left < b.right + gap &&
  b.left < a.right + gap &&
  a.top < b.bottom + gap &&
  b.top < a.bottom + gap;

const inside = (inner: Box, outer: Box) =>
  inner.left >= outer.left &&
  inner.right <= outer.right &&
  inner.top >= outer.top &&
  inner.bottom <= outer.bottom;

/**
 * What's wrong with a card's layout, if anything: words outside the frame,
 * on a motif, the disc or a pattern band, on each other, or too near the
 * signature. Empty when the card is clean.
 */
export function geometryProblems(g: CardGeometry): string[] {
  const problems: string[] = [];
  const words = [g.label, ...g.lines, ...(g.signature ? [g.signature] : [])];
  for (const w of words) {
    if (!inside(w.box, g.safe)) problems.push(`"${w.text}" leaves the card`);
    for (const r of [...g.reserved, ...g.motifs.map((m) => m.box)]) {
      if (overlaps(w.box, r, TEXT_GAP)) {
        problems.push(`"${w.text}" runs into the decoration`);
        break;
      }
    }
  }
  const text = [g.label, ...g.lines];
  for (let i = 0; i < text.length; i += 1) {
    for (let j = i + 1; j < text.length; j += 1) {
      // Lines of one paragraph sit close; only real overlap counts.
      if (overlaps(text[i]!.box, text[j]!.box, -2)) {
        problems.push(`"${text[i]!.text}" overlaps "${text[j]!.text}"`);
      }
    }
    if (g.signature && overlaps(text[i]!.box, g.signature.box, SIGNATURE_GAP)) {
      problems.push(`"${text[i]!.text}" crowds the signature`);
    }
  }
  return problems;
}

// Where measureText can't say (old engines, tests), glyphs are assumed to
// reach this far above and below the baseline, generously for Devanagari.
const sizeOf = (css: string) => Number(/(\d+(?:\.\d+)?)px/.exec(css)?.[1] ?? 0);

/** measureText on a 2D context, with the ink's ascent and descent. */
export function canvasMeasure(ctx: CanvasRenderingContext2D): MeasureText {
  return (text, css) => {
    ctx.font = css;
    const m = ctx.measureText(text);
    const size = sizeOf(css);
    const ascent = Number.isFinite(m.actualBoundingBoxAscent)
      ? m.actualBoundingBoxAscent
      : size * 0.85;
    const descent = Number.isFinite(m.actualBoundingBoxDescent)
      ? m.actualBoundingBoxDescent
      : size * 0.35;
    return { width: m.width, ascent, descent };
  };
}

function place(
  measure: MeasureText,
  text: string,
  css: string,
  y: number,
  extraWidth = 0
): PlacedText {
  const m = measure(text, css);
  const width = m.width + extraWidth;
  return {
    text,
    font: css,
    x: CENTRE,
    y,
    box: {
      left: CENTRE - width / 2,
      right: CENTRE + width / 2,
      top: y - m.ascent,
      bottom: y + m.descent,
    },
  };
}

/**
 * Lays the card out: the greeting (an English part and the part after
 * " · " on their own lines) as large as the layout allows, then smaller
 * until every word clears the signature, the motifs and the frame (D269).
 */
export function cardGeometry(
  card: CardChoice,
  line: string,
  senderName: string,
  measure: MeasureText
): CardGeometry {
  const layout = LAYOUTS[card.design.layout] ?? LAYOUTS.centre;
  const width = CARD_SIZE - PADDING * 2;
  const motifs = layout.motifs(card.design).map((m) => ({
    ...m,
    box: motifBox(m.x, m.y, m.size),
  }));
  // The label is spaced out (6px a letter), which measureText may not count.
  const labelText = card.label.toUpperCase();
  const label = place(
    measure,
    labelText,
    font(700, 36, BODY_FONT),
    layout.labelY,
    6 * labelText.length
  );
  const signatureName = firstName(senderName);
  const signature = signatureName
    ? place(
        measure,
        `— ${signatureName}`,
        font(600, 44, BODY_FONT),
        layout.signatureY
      )
    : null;

  const text = line.trim() || card.fallback;
  const [lead, ...restParts] = text.split(/\s+·\s+/);
  const rest = restParts.join(' · ');
  const widthAt = (weight: number) => (t: string, size: number) =>
    measure(t, font(weight, size, DISPLAY_FONT)).width;

  const attempt = (max: number): CardGeometry => {
    const leadFit = fitText(lead ?? '', {
      maxWidth: width,
      maxLines: rest ? 2 : 3,
      max,
      min: Math.min(max, Math.max(MIN_LEAD, Math.round(max * 0.75))),
      measure: widthAt(800),
    });
    const restFit = rest
      ? fitText(rest, {
          maxWidth: width,
          maxLines: 2,
          max: Math.max(MIN_REST, Math.min(72, Math.round(leadFit.size * 0.7))),
          min: MIN_REST,
          measure: widthAt(700),
        })
      : null;
    const gap = Math.round(leadFit.size * 0.2);
    const leadHeight = leadFit.lines.length * leadFit.size * 1.1;
    const restHeight = restFit
      ? restFit.lines.length * restFit.size * 1.35 + gap
      : 0;
    // Centred in the layout's band for the greeting.
    const band = layout.textBottom - layout.textTop;
    const top =
      layout.textTop + Math.max(0, (band - leadHeight - restHeight) / 2);
    const lines = [
      ...leadFit.lines.map((l, i) =>
        place(
          measure,
          l,
          font(800, leadFit.size, DISPLAY_FONT),
          top + leadFit.size * (0.9 + i * 1.1)
        )
      ),
      ...(restFit?.lines ?? []).map((l, i) =>
        place(
          measure,
          l,
          font(700, restFit!.size, DISPLAY_FONT),
          top + leadHeight + gap + restFit!.size * (1.0 + i * 1.35)
        )
      ),
    ];
    return {
      label,
      lines,
      signature,
      motifs,
      reserved: layout.reserved,
      safe: SAFE,
      size: leadFit.size,
    };
  };

  // Largest first; smaller until it's clean. A greeting that can't fit even
  // at the smallest size is drawn at it (the line is capped at 120
  // characters, and every ready-made wish fits: greetings.render.test.ts).
  let geometry = attempt(layout.leadMax);
  for (
    let max = layout.leadMax - 4;
    max >= MIN_LEAD && geometryProblems(geometry).length > 0;
    max -= 4
  ) {
    geometry = attempt(max);
  }
  return geometry;
}

/**
 * Draws the card onto a square canvas, `size` pixels a side (1080 for the
 * card that's sent, smaller for the sheet's thumbnails): the design's
 * decoration and motifs, the label (the occasion's or the day's name), the
 * greeting and "— Asha", where `cardGeometry` puts them.
 */
export function drawCard(
  canvas: HTMLCanvasElement,
  card: CardChoice,
  line: string,
  senderName: string,
  size = CARD_SIZE
): CardGeometry | null {
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  if (size !== CARD_SIZE) ctx.scale(size / CARD_SIZE, size / CARD_SIZE);
  const { design } = card;
  const layout = LAYOUTS[design.layout] ?? LAYOUTS.centre;
  const geometry = cardGeometry(card, line, senderName, canvasMeasure(ctx));

  ctx.fillStyle = design.ground;
  ctx.fillRect(0, 0, CARD_SIZE, CARD_SIZE);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  layout.decorate(ctx, design);

  ctx.fillStyle = design.ink;
  ctx.textBaseline = 'middle';
  for (const motif of geometry.motifs) {
    ctx.font = font(400, motif.size, EMOJI_FONT);
    ctx.fillText(motif.text, motif.x, motif.y);
  }
  ctx.textBaseline = 'alphabetic';

  // The occasion's name, small and spaced, above the greeting; on the disc
  // layout the accent is the disc, so the name is in the ink.
  ctx.fillStyle = design.layout === 'big-motif' ? design.ink : design.accent;
  ctx.font = geometry.label.font;
  setLetterSpacing(ctx, '6px');
  ctx.fillText(geometry.label.text, geometry.label.x, geometry.label.y);
  setLetterSpacing(ctx, '0px');

  ctx.fillStyle = design.ink;
  for (const placed of geometry.lines) {
    ctx.font = placed.font;
    ctx.fillText(placed.text, placed.x, placed.y);
  }

  if (geometry.signature) {
    ctx.globalAlpha = 0.85;
    ctx.font = geometry.signature.font;
    ctx.fillText(
      geometry.signature.text,
      geometry.signature.x,
      geometry.signature.y
    );
    ctx.globalAlpha = 1;
  }
  return geometry;
}

/** The card as a PNG file, ready for sendAttachment. */
export async function renderCard(
  card: CardChoice,
  line: string,
  senderName: string
): Promise<File> {
  await loadCardFonts(`${line} ${card.label} ${senderName}`);
  const canvas = document.createElement('canvas');
  drawCard(canvas, card, line, senderName);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/png')
  );
  if (!blob) throw new Error('Could not draw the card');
  return new File([blob], `greeting-${card.design.id}.png`, {
    type: 'image/png',
  });
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// letterSpacing on canvas is newer than the rest; skipped where missing.
function setLetterSpacing(ctx: CanvasRenderingContext2D, value: string) {
  if ('letterSpacing' in ctx) {
    (
      ctx as CanvasRenderingContext2D & { letterSpacing: string }
    ).letterSpacing = value;
  }
}
